/**
 * The customer mail in the viewer's language, on the "Original mail" pane.
 *
 * When the mail is not in the viewer's language, the pane shows the translated subject
 * and the translated new text of the body, with "Translated from <language> · Show
 * original" above it; the original is always one click away. The quoted earlier messages
 * stay as written. While the translation is being made, the original is shown and can be
 * read; when it fails, the original stays with one short sentence.
 *
 * The translation is a read: the stored mail is never changed and nothing is sent.
 */

import { Icon, Languages } from "@norican/icons";
import { Alert, Button, Select, Spinner, Typography } from "@norican/react";
import { useState } from "react";

import {
  type CaseMail,
  type CaseTranslation,
  languageName,
  TRANSLATION_UNAVAILABLE_TEXT,
  translationFailureText,
  type TranslationTarget,
  useCaseTranslation,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { track } from "@/lib/usage";
import { USAGE_LANGUAGES, usageWord } from "@/lib/usage-events";

import {
  readViewerLanguage,
  rememberViewerLanguage,
  toViewerLanguage,
  VIEWER_LANGUAGES,
} from "./viewer-language";

/** The note on a translation whose numbers do not all match the original. */
export const CHECK_AGAINST_ORIGINAL_TITLE = "Check against the original";

/** True when the mail has text the server can translate. */
export function canTranslate(mail: CaseMail | null | undefined): boolean {
  if (!mail || !mail.available || mail.body_source === "too_large") {
    return false;
  }
  return (mail.subject ?? "").trim() !== "" || (mail.body_text ?? "").trim() !== "";
}

export interface MailTranslationInput {
  mail: CaseMail;
  translation: CaseTranslation | undefined;
  /** The translation is being made right now. */
  loading: boolean;
  /** The request failed (an `ApiError`), or null. */
  error: unknown;
  /** The viewer chose to see the original. */
  showOriginal: boolean;
}

/** What the pane shows: the translation, the original, and the sentence above the text. */
export interface MailTranslationView {
  /** `translated`: the translation is on screen; `original`: the viewer chose the original. */
  state: "none" | "loading" | "translated" | "original" | "failed";
  subject: string;
  /** The translated new text of the body; null shows the original body. */
  current: string | null;
  /** "Translated from Spanish" or "Original in Spanish". */
  notice: string | null;
  /** Why the original is shown after a failed translation. */
  failure: string | null;
  /** A number or item number of the original is missing in the translation. */
  checkNumbers: boolean;
  /** Only the first part of a long mail was translated. */
  truncated: boolean;
}

/** Decide what the pane shows. Pure, so the rules can be tested without a browser. */
export function mailTranslationView(input: MailTranslationInput): MailTranslationView {
  const { mail, translation, loading, error, showOriginal } = input;
  const original: MailTranslationView = {
    state: "none",
    subject: mail.subject,
    current: null,
    notice: null,
    failure: null,
    checkNumbers: false,
    truncated: false,
  };
  if (error) {
    return { ...original, state: "failed", failure: translationFailureText(error) };
  }
  if (loading && !translation) {
    return { ...original, state: "loading" };
  }
  // `same_language`: already readable; `no_mail`: no stored text, the pane says so itself.
  if (!translation || translation.status === "same_language" || translation.status === "no_mail") {
    return original;
  }
  if (translation.status !== "translated" || translation.body == null) {
    return { ...original, state: "failed", failure: TRANSLATION_UNAVAILABLE_TEXT };
  }
  // A translation without text (nothing new above the quoted messages) shows the original.
  const hasText = (translation.body ?? "").trim() !== "";
  const language =
    translation.source_language_name?.trim() || languageName(translation.source_language);
  if (showOriginal) {
    return {
      ...original,
      state: "original",
      notice: language ? `Original in ${language}` : "Original",
    };
  }
  return {
    ...original,
    state: "translated",
    subject: translation.subject?.trim() ? translation.subject : mail.subject,
    current: hasText ? translation.body : null,
    notice: language ? `Translated from ${language}` : "Translated",
    checkNumbers: translation.numbers_ok === false,
    truncated: translation.truncated === true,
  };
}

/** The viewer language, remembered in this browser. */
export function useViewerLanguage(): [TranslationTarget, (language: TranslationTarget) => void] {
  const [language, setLanguage] = useState<TranslationTarget>(readViewerLanguage);
  const choose = (next: TranslationTarget) => {
    logger.debug("[translation] viewer language", next);
    rememberViewerLanguage(next);
    setLanguage(next);
  };
  return [language, choose];
}

/**
 * The translation state of one case mail for the viewer's language.
 *
 * `auto` (default): the mail is translated when it opens. Off for a mail Norican I2Q does
 * not work on (not an inquiry): no model call until the viewer asks with `request`.
 */
export function useMailTranslation(caseId: string, mail: CaseMail | undefined, auto = true) {
  const [language, chooseLanguage] = useViewerLanguage();
  // The case the viewer switched to the original for: the next case opens translated again.
  const [originalFor, setOriginalFor] = useState<string | null>(null);
  // The case the viewer asked to translate (only read when `auto` is off).
  const [requestedFor, setRequestedFor] = useState<string | null>(null);
  const showOriginal = originalFor === caseId;
  const requested = auto || requestedFor === caseId;
  const query = useCaseTranslation(caseId, language, canTranslate(mail) && requested);
  const view = mail
    ? mailTranslationView({
        mail,
        translation: query.data,
        loading: query.isFetching,
        error: query.error,
        showOriginal,
      })
    : null;
  return {
    language,
    /** True when the viewer may ask for the translation (it is not made on its own). */
    canRequest: !requested && canTranslate(mail) && query.data === undefined,
    request: () => {
      logger.debug("[translation] requested", { caseId });
      track(
        "case.translation",
        { action: "request", lang: usageWord(USAGE_LANGUAGES, language) },
        { caseId },
      );
      setRequestedFor(caseId);
    },
    chooseLanguage: (next: TranslationTarget) => {
      track(
        "case.translation",
        { action: "language", lang: usageWord(USAGE_LANGUAGES, next) },
        { caseId },
      );
      setOriginalFor(null);
      chooseLanguage(next);
    },
    view,
    toggleOriginal: () => {
      logger.debug("[translation] toggle", { caseId, showOriginal: !showOriginal });
      track(
        "case.translation",
        {
          action: showOriginal ? "show_translation" : "show_original",
          lang: usageWord(USAGE_LANGUAGES, language),
        },
        { caseId },
      );
      setOriginalFor(showOriginal ? null : caseId);
    },
  };
}

/** The visible label of the language selector: it chooses the viewer's language, not the mail's. */
export const VIEWER_LANGUAGE_LABEL = "Read mails in";

/** The small language selector on the pane header. */
export function ViewerLanguageSelect({
  value,
  onChange,
}: {
  value: TranslationTarget;
  onChange: (language: TranslationTarget) => void;
}) {
  return (
    <Select.Root
      value={value}
      onValueChange={(next) => {
        const language = toViewerLanguage(next);
        if (language) {
          onChange(language);
        }
      }}
    >
      <Select.Trigger size="sm" aria-label={VIEWER_LANGUAGE_LABEL} className="min-w-32">
        {/* The label is visible: a chip "English" alone next to a German mail read as
            the mail's language (tester round 5, 12 Oct 2026). */}
        <span className="inline-flex items-center gap-1.5">
          <Icon icon={Languages} size="xs" />
          <span className="text-fg-subtle font-normal">{VIEWER_LANGUAGE_LABEL}</span>
          <Select.Value>
            {(code: unknown) =>
              VIEWER_LANGUAGES.find((entry) => entry.code === code)?.label ?? "English"
            }
          </Select.Value>
        </span>
      </Select.Trigger>
      <Select.Portal>
        <Select.Positioner>
          <Select.Popup>
            <Select.List>
              {VIEWER_LANGUAGES.map((entry) => (
                <Select.Item key={entry.code} value={entry.code}>
                  {entry.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

/** The line above the mail text: translating, translated / original toggle, or the fallback. */
export function TranslationNotice({
  view,
  onToggle,
}: {
  view: MailTranslationView;
  onToggle: () => void;
}) {
  if (view.state === "loading") {
    return (
      <div className="text-fg-subtle flex items-center gap-2 text-xs" aria-live="polite">
        <Spinner size="sm" tone="neutral" label="Translating..." />
        <span>Translating...</span>
      </div>
    );
  }
  if (view.state === "failed") {
    return (
      <Typography variant="caption" tone="subtle">
        {view.failure}
      </Typography>
    );
  }
  if (view.state !== "translated" && view.state !== "original") {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="text-fg-muted flex flex-wrap items-center gap-1 text-xs">
        <Icon icon={Languages} size="xs" />
        <span>{view.notice}</span>
        <span aria-hidden>·</span>
        <Button
          variant="ghost"
          tone="accent"
          size="sm"
          className="h-auto px-1 py-0 text-xs"
          onClick={onToggle}
        >
          {view.state === "translated" ? "Show original" : "Show translation"}
        </Button>
      </div>
      {view.state === "translated" && view.checkNumbers && (
        <Alert tone="warning" title={CHECK_AGAINST_ORIGINAL_TITLE}>
          Some numbers or item numbers of the original are not in the translation. Use the original
          for quantities, prices and item numbers.
        </Alert>
      )}
      {view.state === "translated" && view.truncated && (
        <Typography variant="caption" tone="subtle">
          This mail is long. I2Q translated the first part only.
        </Typography>
      )}
    </div>
  );
}
